import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import './PermissionManagement.css';

function PermissionManagement() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [tables, setTables] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('tables');

  // Table permissions state
  const [tablePermissions, setTablePermissions] = useState({});
  
  // Client management permissions state
  const [clientMgmtPerms, setClientMgmtPerms] = useState({
    canManageUsers: false,
    canManageRoles: false,
    canManagePermissions: false,
    canManageMenus: false,
    canViewAuditLog: false,
    canManageSettings: false,
    canManageClientData: false,
    canExportData: false
  });

  // Menu permissions state
  const [menuPerms, setMenuPerms] = useState({});

  useEffect(() => {
    fetchRoles();
    fetchTables();
    fetchMenus();
  }, [user]);

  const fetchRoles = async () => {
    try {
      const response = await api.get(`/admin/roles/client/${user.clientId}`);
      if (response.data.success) {
        setRoles(response.data.roles);
      }
    } catch (err) {
    }
  };

  const fetchTables = async () => {
    try {
      const response = await api.get(`/permissions/client/${user.clientId}/tables`);
      if (response.data.success) {
        setTables(response.data.tables);
      }
    } catch (err) {
    }
  };

  const fetchMenus = async () => {
    try {
      const response = await api.get('/menus/all');
      if (response.data.success) {
        response.data.menus.forEach((m, i) => {
        });
        setMenus(response.data.menus || []);
      } else {
      }
    } catch (err) {
    }
  };

  const handleRoleSelect = async (role) => {
    try {
      setSelectedRole(role);
      setLoading(true);

      const response = await api.get(`/permissions/group/${role.GroupID}`);
      if (response.data.success) {
        // Build table permissions map
        const tablePermsMap = {};
        response.data.tablePermissions.forEach(tp => {
          tablePermsMap[tp.TableName] = {
            canView: tp.CanView,
            canCreate: tp.CanCreate,
            canEdit: tp.CanEdit,
            canDelete: tp.CanDelete,
            canExport: tp.CanExport
          };
        });
        setTablePermissions(tablePermsMap);

        // Build menu permissions map
        const menuPermsMap = {};
        response.data.menuPermissions.forEach(mp => {
          menuPermsMap[mp.MenuItemID || mp.MenuID] = {
            canView: mp.CanView,
            canManage: mp.CanManage
          };
        });
        setMenuPerms(menuPermsMap);

        // Set client management permissions
        if (response.data.clientManagementPermissions) {
          setClientMgmtPerms(response.data.clientManagementPermissions);
        }
      }
    } catch (err) {
      setError('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleTablePermissionChange = (tableName, permission, value) => {
    setTablePermissions(prev => ({
      ...prev,
      [tableName]: {
        ...prev[tableName],
        [permission]: value
      }
    }));
  };

  const handleClientMgmtPermissionChange = (permission, value) => {
    setClientMgmtPerms(prev => ({
      ...prev,
      [permission]: value
    }));
  };

  const handleMenuPermissionChange = (menuId, permission, value) => {
    setMenuPerms(prev => ({
      ...prev,
      [menuId]: {
        ...prev[menuId],
        [permission]: value
      }
    }));
  };

  const saveTablePermissions = async (tableName) => {
    try {
      const perms = tablePermissions[tableName] || {
        canView: false,
        canCreate: false,
        canEdit: false,
        canDelete: false,
        canExport: false
      };

      const response = await api.post(`/permissions/group/${selectedRole.GroupID}/table-permissions`, {
        clientId: user.clientId,
        tableName,
        ...perms
      });

      if (response.data.success) {
        setSuccess(`Permissions saved for ${tableName}`);
        setTimeout(() => setSuccess(null), 2000);
      }
    } catch (err) {
      setError('Failed to save permissions');
    }
  };

  const saveClientMgmtPermissions = async () => {
    try {
      const response = await api.post(
        `/permissions/group/${selectedRole.GroupID}/client-management-permissions`,
        {
          clientId: user.clientId,
          ...clientMgmtPerms
        }
      );

      if (response.data.success) {
        setSuccess('Client management permissions saved');
        setTimeout(() => setSuccess(null), 2000);
      }
    } catch (err) {
      setError('Failed to save permissions');
    }
  };

  const saveMenuPermissions = async (menuId) => {
    try {
      const perms = menuPerms[menuId] || {
        canView: false,
        canManage: false
      };

      const response = await api.post(`/permissions/group/${selectedRole.GroupID}/menu-permissions`, {
        clientId: user.clientId,
        menuId,
        ...perms
      });

      if (response.data.success) {
        setSuccess(`Menu permissions saved`);
        setTimeout(() => setSuccess(null), 2000);
      }
    } catch (err) {
      setError('Failed to save permissions');
    }
  };

  return (
    <div className="permission-management-container">
      <div className="perm-header">
        <h2>Permission Management</h2>
        <p className="text-muted">Configure granular access control for roles</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="perm-layout">
        {/* Role Selector */}
        <div className="role-selector">
          <h3>Roles</h3>
          {roles.length === 0 ? (
            <p className="no-data">No roles found. Create roles first.</p>
          ) : (
            <div className="roles-list">
              {roles.map(role => (
                <div
                  key={role.GroupID}
                  className={`role-card ${selectedRole?.GroupID === role.GroupID ? 'active' : ''}`}
                  onClick={() => handleRoleSelect(role)}
                >
                  <h4>{role.GroupName}</h4>
                  <p>{role.GroupDescription}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Permission Editor */}
        {selectedRole && !loading ? (
          <div className="permission-editor">
            <div className="perm-tabs">
              <button
                className={`tab-btn ${activeTab === 'tables' ? 'active' : ''}`}
                onClick={() => setActiveTab('tables')}
              >
                📊 Table Access
              </button>
            </div>

            <div className="tab-content">
              {/* Tables Tab */}
              {activeTab === 'tables' && (
                <div className="tables-section">
                  <h3>Table Permissions for {selectedRole.GroupName}</h3>
                  {tables.length === 0 ? (
                    <p className="no-data">No tables available</p>
                  ) : (
                    <div className="tables-grid">
                      {tables.map(table => (
                        <div key={table} className="table-perm-card">
                          <h4>{table}</h4>
                          <div className="perm-checkboxes">
                            <label>
                              <input
                                type="checkbox"
                                checked={tablePermissions[table]?.canView || false}
                                onChange={(e) => handleTablePermissionChange(table, 'canView', e.target.checked)}
                              />
                              View
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={tablePermissions[table]?.canCreate || false}
                                onChange={(e) => handleTablePermissionChange(table, 'canCreate', e.target.checked)}
                              />
                              Create
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={tablePermissions[table]?.canEdit || false}
                                onChange={(e) => handleTablePermissionChange(table, 'canEdit', e.target.checked)}
                              />
                              Edit
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={tablePermissions[table]?.canDelete || false}
                                onChange={(e) => handleTablePermissionChange(table, 'canDelete', e.target.checked)}
                              />
                              Delete
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={tablePermissions[table]?.canExport || false}
                                onChange={(e) => handleTablePermissionChange(table, 'canExport', e.target.checked)}
                              />
                              Export
                            </label>
                          </div>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => saveTablePermissions(table)}
                          >
                            Save
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : loading ? (
          <div className="permission-editor">
            <p>Loading permissions...</p>
          </div>
        ) : (
          <div className="permission-editor">
            <p className="no-data">Select a role to manage permissions</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PermissionManagement;
